/**
 * SnippetViewer - Componente para visualizar snippets de PDF en el panel Visor
 */

import React, { useState } from 'react';
import { MapPin, FileText, Copy, ExternalLink, Calendar } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import MarkdownRenderer from './MarkdownRenderer';
import type { PDFSnippet } from '../services/api';

interface SnippetViewerProps {
  snippet: PDFSnippet;
  onOpenInPDFViewer?: (snippet: PDFSnippet) => void;
  onCopy?: (text: string) => void;
}

const SnippetViewer: React.FC<SnippetViewerProps> = ({
  snippet,
  onOpenInPDFViewer,
  onCopy,
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Fecha no disponible';
    try {
      const date = new Date(dateString);
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const handleCopyContent = () => {
    const content = snippet.text_content || '';
    if (content && onCopy) {
      onCopy(content);
      navigator.clipboard.writeText(content);
    }
  };

  const maxInitialChars = 600;
  const shouldShowExpand = snippet.text_content && snippet.text_content.length > maxInitialChars;
  const displayContent = isExpanded
    ? snippet.text_content
    : (shouldShowExpand ? snippet.text_content?.substring(0, maxInitialChars) + '...' : snippet.text_content);

  return (
    <div className={`h-full flex flex-col ${theme.colors.bg.primary} overflow-y-auto scrollbar-thin`}>
      <div className="flex-1 p-4 space-y-4">
        {/* Título */}
        <div>
          <h3 className={`text-lg font-semibold ${theme.colors.text.primary} mb-2`}>
            {snippet.title}
          </h3>
        </div>

        {/* Metadata de localización */}
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-nb-error flex-shrink-0" />
          <span className={`text-sm font-medium ${theme.colors.text.primary}`}>
            Página {snippet.page_number}
          </span>
          {snippet.document_name && (
            <>
              <span className={`text-sm ${theme.colors.text.secondary}`}>•</span>
              <span className={`text-sm ${theme.colors.text.secondary} truncate`}>
                {snippet.document_name}
              </span>
            </>
          )}
        </div>

        {/* Vista previa de imagen */}
        {snippet.image_data && (
          <div className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3`}>
            <img
              src={snippet.image_data}
              alt="Fragmento"
              className="max-w-full max-h-96 rounded object-contain mx-auto"
            />
          </div>
        )}

        {/* Contenido de texto */}
        {snippet.text_content && (() => {
          // Detectar si el contenido es markdown (para Studios o mensajes formateados)
          const isMarkdown = (content: string): boolean => {
            if (!content || typeof content !== 'string') return false;
            const trimmed = content.trim();
            // Verificar si tiene características de markdown
            return (
              trimmed.includes('**') || // Bold
              trimmed.includes('##') || // Headers
              trimmed.includes('###') || // Headers
              trimmed.includes('- ') || // Lists
              trimmed.includes('* ') || // Lists
              trimmed.includes('```') || // Code blocks
              trimmed.includes('[1]') || // Citations
              trimmed.includes('[2]') || // Citations
              (trimmed.startsWith('#') && trimmed.includes('\n')) // Markdown headers
            );
          };

          const contentIsMarkdown = isMarkdown(snippet.text_content);
          const contentToDisplay = isExpanded ? snippet.text_content : (shouldShowExpand ? snippet.text_content.substring(0, maxInitialChars) + '...' : snippet.text_content);

          return (
            <div className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-4`}>
              {contentIsMarkdown ? (
                <div className="prose prose-invert max-w-none">
                  <MarkdownRenderer
                    content={contentToDisplay}
                  />
                </div>
              ) : (
                <div className={`text-sm ${theme.colors.text.primary} leading-relaxed whitespace-pre-wrap`}>
                  {contentToDisplay}
                </div>
              )}
              {shouldShowExpand && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={`mt-2 text-xs font-medium ${theme.colors.text.secondary} hover:${theme.colors.text.primary} transition-colors underline`}
                >
                  {isExpanded ? '↑ Leer menos' : '↓ Leer más'}
                </button>
              )}
            </div>
          );
        })()}

        {/* Notas */}
        {snippet.notes && (
          <div>
            <label className={`text-xs font-medium ${theme.colors.text.secondary} mb-2 block flex items-center gap-1`}>
              <FileText size={14} />
              Notas:
            </label>
            <div className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3`}>
              <p className={`text-sm ${theme.colors.text.primary} whitespace-pre-wrap`}>
                {snippet.notes}
              </p>
            </div>
          </div>
        )}

        {/* Tags */}
        {snippet.tags && snippet.tags.length > 0 && (
          <div>
            <label className={`text-xs font-medium ${theme.colors.text.secondary} mb-2 block`}>
              Etiquetas:
            </label>
            <div className="flex flex-wrap gap-1.5">
              {snippet.tags.map((tag, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-0.5 ${theme.colors.bg.tertiary} ${theme.colors.text.inverse} rounded text-xs`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Fecha */}
        {snippet.created_at && (
          <div className={`text-xs ${theme.colors.text.secondary} flex items-center gap-1`}>
            <Calendar size={12} />
            <span>Guardado: {formatDate(snippet.created_at)}</span>
          </div>
        )}
      </div>

      {/* Footer con acciones */}
      <div className={`flex-shrink-0 border-t ${theme.colors.border.primary} p-3 flex justify-between gap-2 bg-opacity-50 ${theme.colors.bg.primary}`}>
        <div className="flex gap-2">
          {snippet.text_content && (
            <button
              onClick={handleCopyContent}
              className={`px-3 py-1.5 rounded ${theme.colors.bg.secondary} ${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors text-xs font-medium flex items-center gap-2`}
            >
              <Copy size={14} />
              Copiar
            </button>
          )}
          {onOpenInPDFViewer && (
            <button
              onClick={() => onOpenInPDFViewer(snippet)}
              className={`px-3 py-1.5 rounded ${theme.colors.bg.secondary} ${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors text-xs font-medium flex items-center gap-2`}
            >
              <ExternalLink size={14} />
              Ver en PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SnippetViewer;

